import { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Camera, User } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [headline, setHeadline] = useState('');
  const [bio, setBio] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handlePhotoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setError('Photo must be under 5MB'); return; }
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (isLogin) {
        await login(email, password);
      } else {
        await register({ name, email, password, headline, bio });
        // Upload avatar if selected
        if (photoFile) {
          const formData = new FormData();
          formData.append('file', photoFile);
          await axios.post(`${API_URL}/api/auth/avatar`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
        }
      }
      navigate('/community');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(d => d.msg || JSON.stringify(d)).join(' ') : 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6] pt-32 pb-20" data-testid="auth-page">
      <div className="max-w-md mx-auto px-6">
        <div className="text-center mb-10">
          <p className="text-[12px] tracking-[0.3em] uppercase mb-3" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600, color: '#C25E3E' }}>THE COMMUNITY</p>
          <h1 className="text-4xl tracking-tight mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500, color: '#1A1A1A' }}>
            {isLogin ? 'Welcome Back' : 'Join the Movement'}
          </h1>
          <p className="text-sm text-[#4A4A4A]" style={{ fontFamily: "'Lora', serif" }}>
            {isLogin ? 'Sign in to your community account' : 'Connect with 370+ women leaders and thousands of supporters'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5 bg-white p-8 border border-[#1A1A1A]/10" data-testid="auth-form">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-[13px]" data-testid="auth-error" style={{ fontFamily: "'Outfit', sans-serif" }}>{error}</div>
          )}

          {!isLogin && (
            <>
              {/* Profile Photo Upload */}
              <div className="flex justify-center mb-2">
                <button type="button" onClick={() => fileRef.current?.click()} className="relative group" data-testid="photo-upload-btn">
                  <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-dashed border-[#1A1A1A]/20 group-hover:border-[#C25E3E] transition-colors flex items-center justify-center bg-[#FAF9F6]">
                    {photoPreview ? (
                      <img src={photoPreview} alt="Preview" className="w-full h-full object-cover" />
                    ) : (
                      <User size={32} className="text-[#1A1A1A]/20" />
                    )}
                  </div>
                  <div className="absolute bottom-0 right-0 w-8 h-8 bg-[#C25E3E] rounded-full flex items-center justify-center shadow-md group-hover:bg-[#1A1A1A] transition-colors">
                    <Camera size={14} className="text-white" />
                  </div>
                  <input type="file" ref={fileRef} onChange={handlePhotoSelect} accept="image/*" className="hidden" data-testid="photo-input" />
                </button>
              </div>
              <p className="text-center text-[11px] text-[#888] mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {photoPreview ? 'Photo selected!' : 'Add a profile photo (optional)'}
              </p>

              <div>
                <label className="block text-[11px] tracking-[0.15em] uppercase mb-2 text-[#4A4A4A]" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>Full Name *</label>
                <input type="text" value={name} onChange={e => setName(e.target.value)} required className="w-full h-12 px-4 border border-[#1A1A1A]/10 bg-[#FAF9F6] text-[#1A1A1A] text-[14px] outline-none focus:border-[#C25E3E] transition-colors" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="register-name" placeholder="Your full name" />
              </div>
              <div>
                <label className="block text-[11px] tracking-[0.15em] uppercase mb-2 text-[#4A4A4A]" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>Headline</label>
                <input type="text" value={headline} onChange={e => setHeadline(e.target.value)} className="w-full h-12 px-4 border border-[#1A1A1A]/10 bg-[#FAF9F6] text-[#1A1A1A] text-[14px] outline-none focus:border-[#C25E3E] transition-colors" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="register-headline" placeholder="e.g. Entrepreneur, Student, Investor" />
              </div>
              <div>
                <label className="block text-[11px] tracking-[0.15em] uppercase mb-2 text-[#4A4A4A]" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>Short Bio</label>
                <textarea value={bio} onChange={e => setBio(e.target.value)} rows={3} className="w-full px-4 py-3 border border-[#1A1A1A]/10 bg-[#FAF9F6] text-[#1A1A1A] text-[14px] outline-none focus:border-[#C25E3E] transition-colors resize-none" style={{ fontFamily: "'Lora', serif" }} data-testid="register-bio" placeholder="Tell the community about yourself..." />
              </div>
            </>
          )}

          <div>
            <label className="block text-[11px] tracking-[0.15em] uppercase mb-2 text-[#4A4A4A]" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>Email *</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} required className="w-full h-12 px-4 border border-[#1A1A1A]/10 bg-[#FAF9F6] text-[#1A1A1A] text-[14px] outline-none focus:border-[#C25E3E] transition-colors" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="auth-email" placeholder="your@email.com" />
          </div>

          <div>
            <label className="block text-[11px] tracking-[0.15em] uppercase mb-2 text-[#4A4A4A]" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>Password *</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} required className="w-full h-12 px-4 border border-[#1A1A1A]/10 bg-[#FAF9F6] text-[#1A1A1A] text-[14px] outline-none focus:border-[#C25E3E] transition-colors" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="auth-password" placeholder="Min 6 characters" />
          </div>

          <button type="submit" disabled={loading} className="w-full h-14 bg-[#C25E3E] text-white text-[12px] uppercase tracking-[0.18em] transition-all hover:bg-[#1A1A1A] disabled:opacity-50" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }} data-testid="auth-submit">
            {loading ? 'Please wait...' : isLogin ? 'Sign In' : 'Create Account'}
          </button>

          {!isLogin && (
            <p className="text-[11px] text-center text-[#888]" style={{ fontFamily: "'Outfit', sans-serif" }}>
              By joining, you agree to our community guidelines. Be kind, be supportive, be authentic.
            </p>
          )}
        </form>

        <p className="text-center mt-6 text-[14px] text-[#4A4A4A]" style={{ fontFamily: "'Outfit', sans-serif" }}>
          {isLogin ? "Don't have an account? " : 'Already a member? '}
          <button onClick={() => { setIsLogin(!isLogin); setError(''); }} className="text-[#C25E3E] font-semibold hover:underline" data-testid="auth-toggle">
            {isLogin ? 'Join Now' : 'Sign In'}
          </button>
        </p>
      </div>
    </div>
  );
};

export default AuthPage;
